import type { CategoryId, Locale } from '../platform.js';

const categoryNames: Record<CategoryId, Record<Locale, string>> = {
  'audio-video': { en: 'Audio & video', ru: 'Аудио и видео' },
  business: { en: 'Business', ru: 'Бизнес' },
  communication: { en: 'Communication', ru: 'Общение' },
  development: { en: 'Development', ru: 'Разработка' },
  education: { en: 'Education', ru: 'Образование' },
  games: { en: 'Games', ru: 'Игры' },
  graphics: { en: 'Graphics & design', ru: 'Графика и дизайн' },
  health: { en: 'Health & fitness', ru: 'Здоровье и спорт' },
  news: { en: 'News & weather', ru: 'Новости и погода' },
  productivity: { en: 'Productivity', ru: 'Продуктивность' },
  security: { en: 'Security', ru: 'Безопасность' },
  system: { en: 'System', ru: 'Система' },
  utilities: { en: 'Utilities', ru: 'Утилиты' },
};

export function categoryName(id: CategoryId, locale: Locale): string {
  return categoryNames[id][locale] ?? categoryNames[id].en;
}

/**
 * winget tag → Appwin category.
 *
 * Tags are the only classification winget provides, and they are noisy: a
 * single app may carry a dozen, and they vary between versions of the same
 * manifest. The list below maps the tags that actually appear on popular
 * packages; anything unknown falls back to `utilities`, which is a real
 * category rather than an "unknown" bucket.
 */
const wingetTagMap: Record<string, CategoryId> = {
  // media
  audio: 'audio-video',
  video: 'audio-video',
  media: 'audio-video',
  'media-player': 'audio-video',
  'music-production': 'audio-video',
  music: 'audio-video',
  podcast: 'audio-video',
  streaming: 'audio-video',
  'video-editor': 'audio-video',
  'audio-editor': 'audio-video',
  codec: 'audio-video',
  player: 'audio-video',

  // development
  'code-editor': 'development',
  ide: 'development',
  'developer-tools': 'development',
  development: 'development',
  programming: 'development',
  'version-control': 'development',
  git: 'development',
  sdk: 'development',
  compiler: 'development',
  'build-tools': 'development',
  database: 'development',
  'api-client': 'development',
  terminal: 'development',
  shell: 'development',
  'package-manager': 'development',

  // system
  virtualization: 'system',
  'virtual-machine': 'system',
  drivers: 'system',
  'system-tools': 'system',
  'system-utilities': 'system',
  monitoring: 'system',
  'disk-management': 'system',
  boot: 'system',
  'operating-system': 'system',
  firmware: 'system',

  // security
  security: 'security',
  antivirus: 'security',
  'password-manager': 'security',
  encryption: 'security',
  vpn: 'security',
  firewall: 'security',
  'two-factor': 'security',

  // communication
  chat: 'communication',
  messenger: 'communication',
  email: 'communication',
  'e-mail': 'communication',
  browser: 'communication',
  'web-browser': 'communication',
  'file-sharing': 'communication',
  'file-transfer': 'communication',
  voip: 'communication',
  conferencing: 'communication',

  // games
  game: 'games',
  gaming: 'games',
  'game-launcher': 'games',
  emulator: 'games',

  // graphics
  'photo-editing': 'graphics',
  'image-editor': 'graphics',
  graphics: 'graphics',
  '3d-modeling': 'graphics',
  '3d': 'graphics',
  rendering: 'graphics',
  'vector-graphics': 'graphics',
  'screen-capture': 'graphics',
  screenshot: 'graphics',
  cad: 'graphics',
  engineering: 'graphics',
  design: 'graphics',

  // productivity
  'office-suite': 'productivity',
  office: 'productivity',
  'text-editor': 'productivity',
  'note-taking': 'productivity',
  notes: 'productivity',
  'project-management': 'productivity',
  'document-processing': 'productivity',
  pdf: 'productivity',
  spreadsheet: 'productivity',
  calendar: 'productivity',
  'task-management': 'productivity',
  todo: 'productivity',

  // education
  education: 'education',
  learning: 'education',
  'reference-tools': 'education',
  dictionary: 'education',
  flashcards: 'education',

  // health
  'health-fitness': 'health',
  fitness: 'health',
  health: 'health',
  meditation: 'health',

  // news
  news: 'news',
  weather: 'news',
  rss: 'news',

  // business
  finance: 'business',
  accounting: 'business',
  erp: 'business',
  crm: 'business',

  // utilities
  archiver: 'utilities',
  compression: 'utilities',
  compress: 'utilities',
  archive: 'utilities',
  'file-manager': 'utilities',
  'file-utilities': 'utilities',
  backup: 'utilities',
  'remote-desktop': 'utilities',
  networking: 'utilities',
  download: 'utilities',
  torrent: 'utilities',
  clipboard: 'utilities',
  'disk-usage': 'utilities',
  cleaner: 'utilities',
  uninstaller: 'utilities',
  launcher: 'utilities',
  'text-processing': 'utilities',
};

export function mapWingetCategory(tags: readonly string[]): CategoryId {
  for (const raw of tags) {
    const key = raw.trim().toLowerCase().replace(/\s+/g, '-');
    const mapped = wingetTagMap[key];
    if (mapped) return mapped;
  }
  return 'utilities';
}
